import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BrandPanel from "../components/BrandPanel";
import LangToggle from "../components/LangToggle";
import { useLang } from "../context/LangContext";
import { ScrollText, Lock, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const { lang, t, toggle } = useLang();
    const [companyName, setCompanyName] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(companyName, password);
        setLoading(false);
        if (!res.ok) {
            setError(res.error);
            toast.error(res.error);
            return;
        }
        // TOTP challenge → 6-digit verify screen (no QR).
        if (res.requires_totp) {
            navigate("/login/totp", {
                state: { login_token: res.login_token, company_name: companyName },
            });
            return;
        }
        navigate(res.isAdmin ? "/admin/accounts" : "/", { replace: true });
    };

    const iconSide = lang === "ar"
        ? { right: "0.75rem" }
        : { left: "0.75rem" };
    const inputPad = lang === "ar" ? "h-11 pe-10 ps-3" : "h-11 ps-10 pe-3";

    return (
        <div className="relative min-h-screen w-full app-bg" dir={t.dir}>
            <LangToggle lang={lang} onToggle={toggle} t={t} />

            <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-2">
                {/* Hero */}
                <div className="relative hidden p-6 lg:block">
                    <BrandPanel lang={lang} strings={t} />
                </div>

                {/* Form */}
                <div className="flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-12">
                    <div className="w-full max-w-md fade-up">
                        <div className="mb-6 flex items-center gap-3 sm:mb-8">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-neutral-900 text-white sm:h-12 sm:w-12">
                                <ScrollText className="h-5 w-5 sm:h-6 sm:w-6" />
                            </div>
                            <div>
                                <h1 className="font-heading text-xl font-bold sm:text-2xl">{t.brand}</h1>
                                <p className="text-xs text-neutral-500 sm:text-sm">{t.loginAppTagline}</p>
                            </div>
                        </div>

                        <h2 className="font-heading text-2xl font-bold sm:text-3xl">{t.loginTitle}</h2>
                        <p className="mt-2 text-sm text-neutral-500">{t.loginSubtitle}</p>

                        <form onSubmit={submit} className="mt-6 space-y-4 sm:mt-8 sm:space-y-5" data-testid="login-form">
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.companyLabel}</Label>
                                <div className="relative">
                                    <Building2
                                        className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                                        style={iconSide}
                                    />
                                    <Input
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        className={inputPad}
                                        placeholder={t.companyPlaceholder}
                                        required
                                        autoComplete="organization"
                                        data-testid="login-company-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.passwordLabel}</Label>
                                <div className="relative">
                                    <Lock
                                        className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                                        style={iconSide}
                                    />
                                    <Input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className={inputPad}
                                        placeholder={t.passwordPlaceholder}
                                        required
                                        data-testid="login-password-input"
                                    />
                                </div>
                            </div>

                            {error ? (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                                    {error}
                                </div>
                            ) : null}

                            <Button
                                type="submit"
                                disabled={loading}
                                className="h-11 w-full bg-neutral-900 text-white hover:bg-neutral-800"
                                data-testid="login-submit-btn"
                            >
                                {loading ? t.loginSubmitLoading : t.loginSubmit}
                            </Button>
                        </form>

                        <p className="mt-3 text-center text-sm">
                            <Link to="/recover" className="font-semibold text-amber-700 hover:underline" data-testid="go-forgot-link">
                                {t.recoverLink}
                            </Link>
                        </p>

                        <p className="mt-3 text-center text-sm text-neutral-600">
                            {t.loginNoAccount}{" "}
                            <Link to="/register/type" className="font-semibold text-amber-700 hover:underline" data-testid="go-register-link">
                                {t.loginGoRegister}
                            </Link>
                        </p>

                        <p className="mt-2 text-center text-xs text-neutral-500">
                            <Link to="/employee/login" className="font-semibold text-amber-700 hover:underline" data-testid="go-emp-login-link">
                                {t.employeeLoginLink}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
